// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  panelNumber,
  signedPanelNumber,
} from "../core/display.js";
import {
  formatElapsedTime,
} from "../core/format.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  rawMetricDescriptor,
} from "../core/raw-metadata.js";
import {
  cylinderKeys,
  cylinderNumberFromKey,
} from "../core/schema.js";
import {
  engineCylinderKeys,
  projectFlightDataForEngine,
} from "../core/engines.js";
import {
  clampScore,
  elapsedAnchoredSampleForAnalysis,
  median,
  peerAverage,
  pointCylinderValues,
  rollingMedianStep,
  thermalEngineWorkPoint as engineWorkPoint,
} from "./utils.js";
import {
  descentEgtDropoutParameters,
} from "./detectors/descent-egt-dropout.js";
import {
  transientDivergenceParameters,
} from "./detectors/transient-divergence.js";

function bestSpeedKey(data) {
  return ["ias", "tas", "groundSpeed"].find((key) => data.some((point) => Number.isFinite(point[key]))) ?? null;
}

function sampleForAnalysis(data, targetCount = transientDivergenceParameters.maxAnalysisSamples) {
  return elapsedAnchoredSampleForAnalysis(data, targetCount);
}

function endMedianChange(points, key) {
  const values = points.map((point) => point[key]).filter(Number.isFinite);
  if (values.length < Math.max(4, points.length * 0.45)) return null;
  const third = Math.max(2, Math.floor(values.length / 3));
  const start = median(values.slice(0, third));
  const end = median(values.slice(-third));
  return Number.isFinite(start) && Number.isFinite(end) ? end - start : null;
}

function channelSpread(point, keys) {
  const values = keys.map((key) => point?.[key]).filter(Number.isFinite);
  const extent = extentFromValues(values);
  return extent && values.length >= 2 ? extent.max - extent.min : null;
}

function medianSpread(points, keys) {
  return median(points.map((point) => channelSpread(point, keys)));
}

function medianRelativeDelta(points, key, keys) {
  return median(points.map((point) => {
    const value = point[key];
    const peer = peerAverage(point, keys, key);
    return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
  }));
}

function strongestDivergenceCylinders(startSegment, middleSegment, keys) {
  return keys.map((key) => {
    const start = medianRelativeDelta(startSegment, key, keys);
    const middle = medianRelativeDelta(middleSegment, key, keys);
    const change = Number.isFinite(start) && Number.isFinite(middle) ? middle - start : null;
    return {
      key,
      cylinder: cylinderNumberFromKey(key),
      change,
      magnitude: Math.abs(change ?? 0),
    };
  }).filter((item) => Number.isFinite(item.change))
    .sort((a, b) => b.magnitude - a.magnitude)
    .slice(0, 3);
}

function rangeForWindow(points, key) {
  const extent = extentFromValues(points.map((point) => point[key]));
  return extent ? extent.max - extent.min : null;
}

function egtAverageForPoint(point, keys) {
  return averageValues(keys.map((key) => point[key]));
}

function rangeForDerivedWindow(points, valueForPoint) {
  const extent = extentFromValues(points.map(valueForPoint));
  return extent ? extent.max - extent.min : null;
}

function divergencePowerContext(window, egtKeys) {
  const config = transientDivergenceParameters.powerContext;
  const speedKey = bestSpeedKey(window);
  const checks = config.checks.map((check) => {
    if (check.key === "speed" && !speedKey) return null;
    const resolvedCheck = check.key === "speed"
      ? { ...check, key: speedKey, label: rawMetricDescriptor(speedKey)[0] }
      : check;
    return resolvedCheck;
  }).filter(Boolean).map((check) => {
    const change = endMedianChange(window, check.key);
    const range = rangeForWindow(window, check.key);
    const changeRatio = Number.isFinite(change) ? Math.abs(change) / check.changeLimit : 0;
    const rangeRatio = Number.isFinite(range) ? range / check.rangeLimit : 0;
    const ratio = Math.max(changeRatio, rangeRatio);
    return {
      ...check,
      change,
      range,
      ratio,
      available: Number.isFinite(change) || Number.isFinite(range),
    };
  });
  const egtAverageRange = rangeForDerivedWindow(window, (point) => egtAverageForPoint(point, egtKeys));
  const egtAverageRatio = Number.isFinite(egtAverageRange) ? egtAverageRange / config.egtAverageRangeLimitF : 0;
  const strongest = checks.filter((check) => check.available).sort((a, b) => b.ratio - a.ratio)[0] ?? null;
  const directPowerAvailable = checks.some((check) => check.directPower && check.available);
  const movementScore = Math.max(strongest?.ratio ?? 0, egtAverageRatio);
  const penalty = movementScore > 1 ? Math.min(config.penaltyMax, config.penaltyBase + (movementScore - 1) * config.penaltyWeight) : 0;
  const detail = strongest && strongest.ratio > 1
    ? `${strongest.label} moved ${Number.isFinite(strongest.change) ? signedPanelNumber(strongest.change, strongest.unit, strongest.digits) : panelNumber(strongest.range, strongest.unit, strongest.digits)}`
    : Number.isFinite(egtAverageRange) && egtAverageRatio > 1
      ? `EGT average range ${Math.round(egtAverageRange)} F`
      : "Power context stable by logged channels";
  return {
    speedKey,
    checks,
    strongest,
    directPowerAvailable,
    egtAverageRange,
    movementScore,
    penalty,
    stable: movementScore <= 1,
    detail,
  };
}

function divergencePowerPenalty(power, egtLift, chtLift, mode) {
  const config = transientDivergenceParameters.powerContext;
  if (!Number.isFinite(power?.penalty) || power.penalty <= 0) return 0;
  const egtAverageRange = power.egtAverageRange;
  const mostlyPeerRelative = Number.isFinite(egtAverageRange)
    && egtAverageRange <= Math.max(config.mostlyPeerRelativeFloorF, egtLift * config.mostlyPeerRelativeEgtFraction);
  const strongThermalSplit = egtLift >= config.strongThermalEgtLiftF || chtLift >= config.strongThermalChtLiftF;
  if (mode === "plateau" && mostlyPeerRelative && (egtLift >= config.plateauRelativeMinEgtLiftF || strongThermalSplit)) return power.penalty * config.plateauRelativePenaltyScale;
  if (mostlyPeerRelative && strongThermalSplit) return power.penalty * (mode === "sustained" ? config.sustainedRelativeStrongPenaltyScale : config.transientRelativeStrongPenaltyScale);
  if (mode === "sustained" && strongThermalSplit) return power.penalty * config.sustainedStrongPenaltyScale;
  return power.penalty;
}

function addDivergencePlateauCandidates(candidates, data, egtKeys, chtKeys, step) {
  const config = transientDivergenceParameters;
  const plateau = config.plateau;
  if (egtKeys.length < config.minCylinderChannels) return;
  const currentCount = Math.max(plateau.minCurrentSamples, Math.round(plateau.currentWindowSeconds / Math.max(step, 0.25)));
  const lookbackCount = Math.max(plateau.minLookbackSamples, Math.round(plateau.lookbackWindowSeconds / Math.max(step, 0.25)));
  const stride = Math.max(plateau.minStrideSamples, Math.round(currentCount / plateau.strideDivisor));

  for (let start = lookbackCount; start + currentCount <= data.length; start += stride) {
    const baselineWindow = data.slice(start - lookbackCount, start);
    const currentWindow = data.slice(start, start + currentCount);
    const baselineRows = baselineWindow.filter(engineWorkPoint);
    const currentRows = currentWindow.filter(engineWorkPoint);
    if (baselineRows.length < lookbackCount * plateau.minBaselineShare || currentRows.length < currentCount * plateau.minCurrentShare) continue;

    const egtStart = medianSpread(baselineRows, egtKeys);
    const egtMiddle = medianSpread(currentRows, egtKeys);
    const chtStart = medianSpread(baselineRows, chtKeys);
    const chtMiddle = medianSpread(currentRows, chtKeys);
    const egtLift = Number.isFinite(egtStart) && Number.isFinite(egtMiddle) ? egtMiddle - egtStart : 0;
    const chtLift = Number.isFinite(chtStart) && Number.isFinite(chtMiddle) ? chtMiddle - chtStart : 0;
    const egtHighPlateau = egtMiddle >= plateau.egtHighPlateauSpreadF && egtLift >= plateau.egtHighPlateauLiftF;
    const strongerPlateau = egtMiddle >= plateau.strongerPlateauSpreadF && egtLift >= plateau.strongerPlateauLiftF;
    const chtHighPlateau = chtMiddle >= plateau.chtHighPlateauSpreadF && chtLift >= plateau.chtHighPlateauLiftF;
    if (!egtHighPlateau && !strongerPlateau && !chtHighPlateau) continue;

    const window = [...baselineWindow.slice(-Math.max(plateau.minContextLookbackSamples, Math.round(plateau.contextLookbackSeconds / Math.max(step, 0.25)))), ...currentWindow];
    const power = divergencePowerContext(window, egtKeys);
    const effectiveEgtLift = Math.max(0, egtLift);
    const effectiveChtLift = Math.max(0, chtLift);
    const powerPenalty = divergencePowerPenalty(power, effectiveEgtLift, effectiveChtLift, "plateau");
    const duration = (currentWindow.at(-1)?.elapsedSeconds ?? start + currentCount - 1) - (currentWindow[0]?.elapsedSeconds ?? start);
    const egtCylinders = strongestDivergenceCylinders(baselineRows, currentRows, egtKeys);
    const chtCylinders = strongestDivergenceCylinders(baselineRows, currentRows, chtKeys);
    const cylinderSet = new Set([...egtCylinders, ...chtCylinders]
      .filter((item) => item.magnitude >= config.cylinderChangeThresholdF)
      .map((item) => item.cylinder)
      .filter(Number.isFinite));
    const baseSuspicion = Math.max(0, egtMiddle - plateau.suspicion.egtSpreadBaseF) * plateau.suspicion.egtSpreadWeight
      + Math.max(0, effectiveEgtLift - plateau.suspicion.egtLiftBaseF) * plateau.suspicion.egtLiftWeight
      + Math.max(0, effectiveChtLift - plateau.suspicion.chtLiftBaseF) * plateau.suspicion.chtLiftWeight
      + (duration >= plateau.suspicion.durationBonusSeconds ? plateau.suspicion.durationBonus : 0)
      + (power.stable ? plateau.suspicion.stablePowerBonus : 0)
      - powerPenalty;
    const chtCorroboration = chtKeys.length >= config.minCylinderChannels
      ? effectiveChtLift >= plateau.chtCorroboration.strongLiftF ? plateau.chtCorroboration.strongMultiplier : effectiveChtLift >= plateau.chtCorroboration.moderateLiftF ? plateau.chtCorroboration.moderateMultiplier : plateau.chtCorroboration.weakMultiplier
      : 1;
    const suspicion = baseSuspicion * chtCorroboration;
    if (suspicion < plateau.minSuspicion) continue;

    candidates.push({
      startTime: currentWindow[0]?.elapsedSeconds ?? start,
      endTime: currentWindow.at(-1)?.elapsedSeconds ?? start + currentCount - 1,
      duration,
      egtStart,
      egtMiddle,
      egtEnd: egtMiddle,
      chtStart,
      chtMiddle,
      chtEnd: chtMiddle,
      egtLift: effectiveEgtLift,
      chtLift: effectiveChtLift,
      egtOnsetLift: effectiveEgtLift,
      chtOnsetLift: effectiveChtLift,
      egtReconvergence: 0,
      chtReconvergence: 0,
      mode: "plateau",
      power,
      powerPenalty,
      egtCylinders,
      chtCylinders,
      cylinders: [...cylinderSet],
      suspicion,
    });
  }
}

function clusteredDivergenceEvents(candidates) {
  const config = transientDivergenceParameters;
  const sorted = [...candidates]
    .filter((candidate) => Number.isFinite(candidate.startTime) && Number.isFinite(candidate.endTime))
    .sort((a, b) => a.startTime - b.startTime || b.suspicion - a.suspicion);
  const clusters = [];
  sorted.forEach((candidate) => {
    const last = clusters.at(-1);
    if (last && candidate.startTime <= last.endTime + config.clusterMergeGapSeconds) {
      last.endTime = Math.max(last.endTime, candidate.endTime);
      last.items.push(candidate);
      if (candidate.suspicion > last.best.suspicion) last.best = candidate;
      return;
    }
    clusters.push({
      startTime: candidate.startTime,
      endTime: candidate.endTime,
      best: candidate,
      items: [candidate],
    });
  });
  return clusters.map((cluster) => ({
    ...cluster.best,
    clusterStartTime: cluster.startTime,
    clusterEndTime: cluster.endTime,
    clusterCount: cluster.items.length,
  })).sort((a, b) => b.suspicion - a.suspicion);
}

function analyzeTransientDivergenceForKeys(data, egtSourceKeys, chtSourceKeys) {
  const config = transientDivergenceParameters;
  const analysisData = sampleForAnalysis(data);
  const egtKeys = egtSourceKeys.filter((key) => analysisData.some((point) => Number.isFinite(point[key])));
  const chtKeys = chtSourceKeys.filter((key) => analysisData.some((point) => Number.isFinite(point[key])));
  if (egtKeys.length < config.minCylinderChannels && chtKeys.length < config.minCylinderChannels) {
    return { key: "divergence", score: null, note: "Need multiple EGT or CHT channels", details: {} };
  }

  const workData = analysisData.filter(engineWorkPoint);
  if (workData.length < config.minEngineRunSamples) {
    return { key: "divergence", score: null, note: "Need engine-run samples", details: { rows: workData.length } };
  }

  const step = rollingMedianStep(analysisData);
  const sampleCount = Math.max(config.minWindowSamples, Math.round(config.windowSeconds / Math.max(step, 0.25)));
  const stride = Math.max(config.minStrideSamples, Math.round(sampleCount / config.strideDivisor));
  const candidates = [];

  for (let start = 0; start + sampleCount <= analysisData.length; start += stride) {
    const window = analysisData.slice(start, start + sampleCount);
    const activeRows = window.filter(engineWorkPoint);
    if (activeRows.length < sampleCount * config.minActiveWindowShare) continue;

    const third = Math.max(config.minSegmentSamples, Math.floor(window.length / config.segmentDivisor));
    const startSegment = window.slice(0, third).filter(engineWorkPoint);
    const middleSegment = window.slice(third, third * 2).filter(engineWorkPoint);
    const endSegment = window.slice(-third).filter(engineWorkPoint);
    if (startSegment.length < third * config.minActiveSegmentShare || middleSegment.length < third * config.minActiveSegmentShare || endSegment.length < third * config.minActiveSegmentShare) continue;

    const egtStart = medianSpread(startSegment, egtKeys);
    const egtMiddle = medianSpread(middleSegment, egtKeys);
    const egtEnd = medianSpread(endSegment, egtKeys);
    const chtStart = medianSpread(startSegment, chtKeys);
    const chtMiddle = medianSpread(middleSegment, chtKeys);
    const chtEnd = medianSpread(endSegment, chtKeys);
    const egtBaseline = Math.min(egtStart ?? Infinity, egtEnd ?? Infinity);
    const chtBaseline = Math.min(chtStart ?? Infinity, chtEnd ?? Infinity);
    const egtLift = Number.isFinite(egtMiddle) && Number.isFinite(egtBaseline) ? egtMiddle - egtBaseline : 0;
    const chtLift = Number.isFinite(chtMiddle) && Number.isFinite(chtBaseline) ? chtMiddle - chtBaseline : 0;
    const egtReconvergence = Number.isFinite(egtMiddle) && Number.isFinite(egtEnd) ? egtMiddle - egtEnd : 0;
    const chtReconvergence = Number.isFinite(chtMiddle) && Number.isFinite(chtEnd) ? chtMiddle - chtEnd : 0;
    const egtLate = Math.max(egtMiddle ?? -Infinity, egtEnd ?? -Infinity);
    const chtLate = Math.max(chtMiddle ?? -Infinity, chtEnd ?? -Infinity);
    const egtOnsetLift = Number.isFinite(egtLate) && Number.isFinite(egtStart) ? egtLate - egtStart : 0;
    const chtOnsetLift = Number.isFinite(chtLate) && Number.isFinite(chtStart) ? chtLate - chtStart : 0;
    const hasLift = egtLift >= config.minEgtLiftF || chtLift >= config.minChtLiftF || egtLift + chtLift * config.combinedLiftChtWeight >= config.minCombinedLiftF;
    const hasTransientShape = egtReconvergence >= config.minEgtReconvergenceF || chtReconvergence >= config.minChtReconvergenceF || (egtLift >= config.strongEgtLiftF && egtEnd <= egtStart + egtLift * config.strongEgtEndFraction);
    const hasSustainedSplit = egtOnsetLift >= config.minEgtOnsetLiftF
      || chtOnsetLift >= config.minChtOnsetLiftF
      || egtOnsetLift + chtOnsetLift * config.sustainedSplitChtWeight >= config.minSustainedCombinedLiftF;
    if ((!hasLift || !hasTransientShape) && !hasSustainedSplit) continue;
    const mode = hasLift && hasTransientShape ? "transient" : "sustained";
    const effectiveEgtLift = mode === "sustained" ? Math.max(egtLift, egtOnsetLift) : egtLift;
    const effectiveChtLift = mode === "sustained" ? Math.max(chtLift, chtOnsetLift) : chtLift;
    const splitSegment = mode === "sustained" && egtEnd > (egtMiddle ?? 0) + config.sustainedEndSelectionBufferF ? endSegment : middleSegment;

    const power = divergencePowerContext(window, egtKeys);
    const egtCylinders = strongestDivergenceCylinders(startSegment, splitSegment, egtKeys);
    const chtCylinders = strongestDivergenceCylinders(startSegment, splitSegment, chtKeys);
    const cylinderSet = new Set([...egtCylinders, ...chtCylinders]
      .filter((item) => item.magnitude >= config.cylinderChangeThresholdF)
      .map((item) => item.cylinder)
      .filter(Number.isFinite));
    const duration = (window.at(-1)?.elapsedSeconds ?? start + sampleCount - 1) - (window[0]?.elapsedSeconds ?? start);
    const powerPenalty = divergencePowerPenalty(power, effectiveEgtLift, effectiveChtLift, mode);
    const baseSuspicion = Math.max(0, effectiveEgtLift - config.suspicion.egtLiftBaseF) * config.suspicion.egtLiftWeight
      + Math.max(0, effectiveChtLift - config.suspicion.chtLiftBaseF) * config.suspicion.chtLiftWeight
      + Math.max(0, egtReconvergence - config.suspicion.egtReconvergenceBaseF) * config.suspicion.egtReconvergenceWeight
      + Math.max(0, chtReconvergence - config.suspicion.chtReconvergenceBaseF) * config.suspicion.chtReconvergenceWeight
      + (duration >= config.suspicion.durationBonusSeconds ? config.suspicion.durationBonus : 0)
      + (mode === "sustained" && duration >= config.suspicion.sustainedDurationBonusSeconds ? config.suspicion.sustainedDurationBonus : 0)
      + (power.stable ? config.suspicion.stablePowerBonus : 0)
      + (!power.directPowerAvailable ? -config.suspicion.missingDirectPowerPenalty : 0)
      - powerPenalty;
    const chtCorroboration = chtKeys.length >= config.minCylinderChannels
      ? effectiveChtLift >= config.chtCorroboration.strongLiftF ? config.chtCorroboration.strongMultiplier : effectiveChtLift >= config.chtCorroboration.moderateLiftF ? config.chtCorroboration.moderateMultiplier : config.chtCorroboration.weakMultiplier
      : 1;
    const suspicion = baseSuspicion * chtCorroboration;
    if (suspicion < config.minSuspicion) continue;

    candidates.push({
      startTime: window[0]?.elapsedSeconds ?? start,
      endTime: window.at(-1)?.elapsedSeconds ?? start + sampleCount - 1,
      duration,
      egtStart,
      egtMiddle,
      egtEnd,
      chtStart,
      chtMiddle,
      chtEnd,
      egtLift: effectiveEgtLift,
      chtLift: effectiveChtLift,
      egtOnsetLift,
      chtOnsetLift,
      egtReconvergence,
      chtReconvergence,
      mode,
      power,
      powerPenalty,
      egtCylinders,
      chtCylinders,
      cylinders: [...cylinderSet],
      suspicion,
    });
  }
  addDivergencePlateauCandidates(candidates, analysisData, egtKeys, chtKeys, step);

  const events = clusteredDivergenceEvents(candidates);
  const top = events[0] ?? null;
  if (!top) {
    return {
      key: "divergence",
      score: null,
      note: "No transient EGT/CHT divergence",
      details: { windows: candidates.length, workSamples: workData.length },
    };
  }

  const eventCount = events.filter((event) => event.suspicion >= Math.max(config.minSuspicion, top.suspicion * config.eventCountShareOfTop)).length;
  const note = `${eventCount > 1 ? `${eventCount} divergence events; strongest` : "Trace divergence"} ${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}, EGT +${Math.round(top.egtLift)} F, CHT +${Math.round(top.chtLift)} F`;
  return {
    key: "divergence",
    score: null,
    note,
    details: {
      top,
      candidates,
      events,
      workSamples: workData.length,
      eventCount,
    },
  };
}

function medianAtEdges(points, key) {
  const values = points.map((point) => point[key]).filter(Number.isFinite);
  if (values.length < Math.max(4, points.length * 0.45)) return null;
  const third = Math.max(2, Math.floor(values.length / 3));
  const start = median(values.slice(0, third));
  const end = median(values.slice(-third));
  return Number.isFinite(start) && Number.isFinite(end) ? { start, end, change: end - start } : null;
}

function descentWindowContext(window) {
  const config = descentEgtDropoutParameters.context;
  const fuel = medianAtEdges(window, "fuelFlow");
  const map = medianAtEdges(window, "map");
  const rpm = medianAtEdges(window, "rpm");
  const altitude = medianAtEdges(window, "altitude");
  const phaseShare = window.filter((point) => /descent/i.test(String(point.phase ?? ""))).length / Math.max(window.length, 1);
  const fuelDrop = Number.isFinite(fuel?.change)
    && fuel.change <= -config.minFuelDropGph
    && (!Number.isFinite(fuel.start) || Math.abs(fuel.change) >= Math.max(config.minFuelDropGph, fuel.start * config.minFuelDropStartFraction));
  const mapDrop = Number.isFinite(map?.change) && map.change <= -config.minMapDropInHg;
  const rpmDrop = Number.isFinite(rpm?.change) && rpm.change <= -config.minRpmDrop;
  const altitudeDrop = Number.isFinite(altitude?.change) && altitude.change <= -config.minAltitudeDropFt;
  const powerReduction = fuelDrop || mapDrop || (rpmDrop && (fuelDrop || mapDrop || phaseShare >= config.minRpmOnlyDescentPhaseShare));
  const descent = phaseShare >= config.minDescentPhaseShare || altitudeDrop;
  const strongest = [
    fuelDrop ? `fuel flow ${signedPanelNumber(fuel.change, " GPH", 1)}` : "",
    mapDrop ? `MAP ${signedPanelNumber(map.change, " inHg", 1)}` : "",
    rpmDrop ? `RPM ${signedPanelNumber(rpm.change, " RPM", 0)}` : "",
    altitudeDrop ? `altitude ${signedPanelNumber(altitude.change, " ft", 0)}` : "",
  ].filter(Boolean);
  return {
    fuel,
    map,
    rpm,
    altitude,
    phaseShare,
    powerReduction,
    descent,
    detail: strongest.length ? strongest.join("; ") : "No logged low-power descent trigger",
  };
}

function dropoutForCylinder(window, egtKeys, key) {
  const config = descentEgtDropoutParameters.dropoutShape;
  const rows = window.map((point) => {
    const value = point[key];
    const peers = egtKeys.filter((peerKey) => peerKey !== key).map((peerKey) => point[peerKey]).filter(Number.isFinite);
    const peerMedian = median(peers);
    if (!Number.isFinite(value) || !Number.isFinite(peerMedian) || peers.length < 2) return null;
    return { value, peerMedian, gap: peerMedian - value };
  }).filter(Boolean);
  if (rows.length < Math.max(config.minRows, window.length * config.minRowsShare)) return null;
  const third = Math.max(config.minSegmentRows, Math.floor(rows.length / config.segmentDivisor));
  const baselineGap = median(rows.slice(0, third).map((row) => row.gap));
  const worst = rows.reduce((best, row, index) => (!best || row.gap > best.row.gap ? { row, index } : best), null);
  const endGap = median(rows.slice(-third).map((row) => row.gap));
  const startValue = median(rows.slice(0, third).map((row) => row.value));
  const worstValue = worst?.row?.value;
  const startPeer = median(rows.slice(0, third).map((row) => row.peerMedian));
  const worstPeer = worst?.row?.peerMedian;
  const dropoutF = Number.isFinite(worst?.row?.gap) && Number.isFinite(baselineGap) ? worst.row.gap - baselineGap : null;
  const candidateDrop = Number.isFinite(startValue) && Number.isFinite(worstValue) ? startValue - worstValue : null;
  const peerDrop = Number.isFinite(startPeer) && Number.isFinite(worstPeer) ? startPeer - worstPeer : null;
  const relativeDrop = Number.isFinite(candidateDrop) && Number.isFinite(peerDrop) ? candidateDrop - peerDrop : null;
  const recovery = Number.isFinite(worst?.row?.gap) && Number.isFinite(endGap) ? worst.row.gap - endGap : 0;
  return {
    key,
    cylinder: cylinderNumberFromKey(key),
    baselineGap,
    endGap,
    dropoutF,
    candidateDrop,
    peerDrop,
    relativeDrop,
    recovery,
  };
}

function analyzeDescentEgtDropoutForKeys(data, egtSourceKeys) {
  const config = descentEgtDropoutParameters;
  const analysisData = sampleForAnalysis(data);
  const egtKeys = egtSourceKeys.filter((key) => analysisData.some((point) => Number.isFinite(point[key])));
  if (egtKeys.length < config.minEgtChannels) {
    return { key: "descentEgtDropout", score: null, note: "Need multiple EGT channels", details: {} };
  }
  const workData = analysisData.filter(engineWorkPoint);
  if (workData.length < config.minEngineRunSamples) {
    return { key: "descentEgtDropout", score: null, note: "Need engine-run samples", details: { rows: workData.length } };
  }

  const step = rollingMedianStep(analysisData);
  const sampleCount = Math.max(config.minWindowSamples, Math.round(config.windowSeconds / Math.max(step, 0.25)));
  const stride = Math.max(config.minStrideSamples, Math.round(sampleCount / config.strideDivisor));
  const candidates = [];
  for (let start = 0; start + sampleCount <= analysisData.length; start += stride) {
    const window = analysisData.slice(start, start + sampleCount);
    if (window.filter(engineWorkPoint).length < sampleCount * config.minEngineWorkWindowShare) continue;
    const context = descentWindowContext(window);
    if (!context.descent || !context.powerReduction) continue;
    const summaries = egtKeys.map((key) => dropoutForCylinder(window, egtKeys, key)).filter(Boolean);
    const top = summaries
      .filter((summary) => Number.isFinite(summary.dropoutF) && Number.isFinite(summary.relativeDrop))
      .sort((a, b) => b.dropoutF - a.dropoutF)[0];
    if (!top) continue;
    const dropoutStrong = top.dropoutF >= config.dropoutShape.strongDropoutF
      || (top.dropoutF >= config.dropoutShape.moderateDropoutF && top.relativeDrop >= config.dropoutShape.moderateRelativeDropF);
    const oneTraceShape = summaries
      .filter((summary) => Number.isFinite(summary.dropoutF) && summary.dropoutF >= Math.max(config.dropoutShape.oneTraceMinDropoutF, top.dropoutF * config.dropoutShape.oneTraceTopShare))
      .length <= config.dropoutShape.maxSimilarTraceCount;
    if (!dropoutStrong || !oneTraceShape) continue;
    const duration = (window.at(-1)?.elapsedSeconds ?? start + sampleCount - 1) - (window[0]?.elapsedSeconds ?? start);
    const suspicion = Math.max(0, top.dropoutF - config.suspicion.dropoutBaseF) * config.suspicion.dropoutWeight
      + Math.max(0, top.relativeDrop - config.suspicion.relativeDropBaseF) * config.suspicion.relativeDropWeight
      + Math.min(config.suspicion.recoveryBonusMax, Math.max(0, top.recovery) * config.suspicion.recoveryWeight)
      + (context.phaseShare >= config.suspicion.descentPhaseBonusShare ? config.suspicion.descentPhaseBonus : 0)
      + (Number.isFinite(context.fuel?.change) ? config.suspicion.fuelContextBonus : 0)
      + (Number.isFinite(context.map?.change) ? config.suspicion.mapContextBonus : 0);
    if (suspicion < config.suspicion.minSuspicion) continue;
    candidates.push({
      ...top,
      startTime: window[0]?.elapsedSeconds ?? start,
      endTime: window.at(-1)?.elapsedSeconds ?? start + sampleCount - 1,
      duration,
      context,
      suspicion,
    });
  }

  const events = clusteredDivergenceEvents(candidates);
  const top = events[0] ?? null;
  if (!top) {
    return {
      key: "descentEgtDropout",
      score: null,
      note: "No descent EGT dropout",
      details: { candidates: candidates.length, workSamples: workData.length },
    };
  }
  const eventCount = events.filter((event) => event.suspicion >= Math.max(config.suspicion.minSuspicion, top.suspicion * config.suspicion.eventCountShareOfTop)).length;
  const note = "EGT trace cooled faster than the group during low-power descent";
  return {
    key: "descentEgtDropout",
    score: null,
    note,
    details: { top, events, candidates, workSamples: workData.length, eventCount },
  };
}

function perEngineDivergence(data, descriptors, helper, prefix) {
  return descriptors.map((engine) => {
    const egtSourceKeys = engineCylinderKeys(engine, "egt");
    const chtSourceKeys = prefix === "transient" ? engineCylinderKeys(engine, "cht") : null;
    if (egtSourceKeys.length === 0 && (!chtSourceKeys || chtSourceKeys.length === 0)) return null;
    const projected = projectFlightDataForEngine(data, engine);
    const result = prefix === "transient"
      ? helper(projected, egtSourceKeys, chtSourceKeys)
      : helper(projected, egtSourceKeys);
    return {
      engineKey: engine.engineKey ?? engine.id ?? `engine${(engine.index ?? 0) + 1}`,
      label: engine.label,
      shortLabel: engine.shortLabel ?? engine.label,
      result,
    };
  }).filter(Boolean);
}

function chooseWorstEngine(perEngine) {
  return perEngine.reduce((acc, entry) => {
    if (acc === null) return entry;
    const accScore = Number.isFinite(acc.result.score) ? acc.result.score : 100;
    const entryScore = Number.isFinite(entry.result.score) ? entry.result.score : 100;
    return entryScore < accScore ? entry : acc;
  }, null);
}

export function analyzeTransientDivergence(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = perEngineDivergence(data, descriptors, analyzeTransientDivergenceForKeys, "transient");
    if (perEngine.length >= 2) {
      const mergedEvents = perEngine.flatMap((entry) =>
        (entry.result.details?.events ?? []).map((event) => ({
          ...event,
          engineKey: entry.engineKey,
          engineLabel: entry.label,
          engineShortLabel: entry.shortLabel,
        })),
      ).sort((a, b) => (b.suspicion ?? 0) - (a.suspicion ?? 0));
      const top = mergedEvents[0] ?? null;
      const eventCount = top
        ? mergedEvents.filter((event) => event.suspicion >= Math.max(transientDivergenceParameters.minSuspicion, top.suspicion * transientDivergenceParameters.eventCountShareOfTop)).length
        : 0;
      const strongestEngine = top ? perEngine.find((entry) => entry.engineKey === top.engineKey) : null;
      const note = top
        ? `${top.engineShortLabel ? `${top.engineShortLabel}: ` : ""}Trace divergence ${formatElapsedTime(top.startTime)}-${formatElapsedTime(top.endTime)}, EGT +${Math.round(top.egtLift ?? 0)} F, CHT +${Math.round(top.chtLift ?? 0)} F`
        : "No transient EGT/CHT divergence";
      return {
        key: "divergence",
        score: null,
        note,
        details: {
          ...(strongestEngine?.result.details ?? {}),
          top,
          events: mergedEvents,
          engines: perEngine.map((entry) => ({
            engineKey: entry.engineKey,
            label: entry.label,
            shortLabel: entry.shortLabel,
            score: null,
            note: entry.result.note,
            details: entry.result.details,
          })),
          eventCount,
          strongestEngineKey: strongestEngine?.engineKey ?? null,
        },
      };
    }
    if (perEngine.length === 1) return perEngine[0].result;
  }
  return analyzeTransientDivergenceForKeys(data, cylinderKeys("egt"), cylinderKeys("cht"));
}

export function analyzeDescentEgtDropout(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = perEngineDivergence(data, descriptors, analyzeDescentEgtDropoutForKeys, "descent");
    if (perEngine.length >= 2) {
      const mergedEvents = perEngine.flatMap((entry) =>
        (entry.result.details?.events ?? []).map((event) => ({
          ...event,
          engineKey: entry.engineKey,
          engineLabel: entry.label,
          engineShortLabel: entry.shortLabel,
        })),
      ).sort((a, b) => (b.suspicion ?? 0) - (a.suspicion ?? 0));
      const top = mergedEvents[0] ?? null;
      const eventCount = top
        ? mergedEvents.filter((event) => event.suspicion >= Math.max(descentEgtDropoutParameters.suspicion.minSuspicion, top.suspicion * descentEgtDropoutParameters.suspicion.eventCountShareOfTop)).length
        : 0;
      const strongestEngine = top ? perEngine.find((entry) => entry.engineKey === top.engineKey) : null;
      const note = top
        ? `${top.engineShortLabel ? `${top.engineShortLabel}: ` : ""}EGT trace cooled faster than the group during low-power descent`
        : "No descent EGT dropout";
      return {
        key: "descentEgtDropout",
        score: null,
        note,
        details: {
          ...(strongestEngine?.result.details ?? {}),
          top,
          events: mergedEvents,
          engines: perEngine.map((entry) => ({
            engineKey: entry.engineKey,
            label: entry.label,
            shortLabel: entry.shortLabel,
            score: null,
            note: entry.result.note,
            details: entry.result.details,
          })),
          eventCount,
          strongestEngineKey: strongestEngine?.engineKey ?? null,
        },
      };
    }
    if (perEngine.length === 1) return perEngine[0].result;
  }
  return analyzeDescentEgtDropoutForKeys(data, cylinderKeys("egt"));
}
